/**
 * Non-blocking toast notification system.
 * Replaces alert() calls with accessible, auto-dismissing notifications.
 */

/** Duration in milliseconds before a toast auto-dismisses */
const DEFAULT_DURATION_MS = 5000;

/** Duration for error toasts (longer to ensure user sees them) */
const ERROR_DURATION_MS = 8000;

/** Maximum number of toasts visible at once */
const MAX_VISIBLE_TOASTS = 5;

export const ToastType = {
    Info: 'info',
    Warning: 'warning',
    Error: 'error',
    Success: 'success',
} as const;

export type ToastType = (typeof ToastType)[keyof typeof ToastType];

type ToastOptions = {
    /** Duration in ms before auto-dismiss. 0 = no auto-dismiss. */
    duration?: number;
};

type ActiveToast = {
    element: HTMLElement;
    timerId: ReturnType<typeof setTimeout> | null;
};

/** Container element ID */
const CONTAINER_ID = 'toastContainer';

/** Active toasts tracked for cleanup */
const activeToasts: ActiveToast[] = [];

/**
 * Ensure the toast container element exists in the DOM.
 */
function ensureContainer(): HTMLElement {
    let container = document.getElementById(CONTAINER_ID);
    if (!container) {
        container = document.createElement('div');
        container.id = CONTAINER_ID;
        container.setAttribute('role', 'status');
        container.setAttribute('aria-live', 'polite');
        document.body.appendChild(container);
    }
    return container;
}

/**
 * Dismiss a toast element with a fade-out animation.
 */
function dismissToast(toast: ActiveToast): void {
    const idx = activeToasts.indexOf(toast);
    if (idx === -1) return; // already dismissed

    if (toast.timerId !== null) {
        clearTimeout(toast.timerId);
    }

    toast.element.classList.add('toast-exit');

    let cleaned = false;
    const removeFromDom = () => {
        if (cleaned) return;
        cleaned = true;
        if (toast.element.parentNode) {
            toast.element.remove();
        }
        const removeIdx = activeToasts.indexOf(toast);
        if (removeIdx !== -1) {
            activeToasts.splice(removeIdx, 1);
        }
    };

    // Remove after CSS transition completes (once: true auto-removes the listener)
    toast.element.addEventListener(
        'transitionend',
        () => {
            clearTimeout(fallbackTimer);
            removeFromDom();
        },
        { once: true },
    );

    // Fallback removal in case transitionend doesn't fire
    const fallbackTimer = setTimeout(removeFromDom, 400);
}

/**
 * Show a toast notification.
 *
 * @param type - The type of notification (info, warning, error, success)
 * @param message - The message text to display
 * @param options - Optional configuration
 */
export function showToast(type: ToastType, message: string, options?: ToastOptions): void {
    const container = ensureContainer();

    // Evict oldest toasts if at capacity
    while (activeToasts.length >= MAX_VISIBLE_TOASTS) {
        const oldest = activeToasts[0];
        dismissToast(oldest);
    }

    // Create toast element
    const toastEl = document.createElement('div');
    toastEl.className = `toast toast-${type}`;
    toastEl.setAttribute('role', 'alert');

    // Icon
    const iconEl = document.createElement('span');
    iconEl.className = 'toast-icon';
    iconEl.textContent = getIcon(type);
    iconEl.setAttribute('aria-hidden', 'true');

    // Message
    const msgEl = document.createElement('span');
    msgEl.className = 'toast-message';
    msgEl.textContent = message;

    // Close button
    const closeEl = document.createElement('button');
    closeEl.className = 'toast-close';
    closeEl.textContent = '×';
    closeEl.setAttribute('aria-label', 'Dismiss notification');
    closeEl.title = 'Dismiss';

    toastEl.appendChild(iconEl);
    toastEl.appendChild(msgEl);
    toastEl.appendChild(closeEl);
    container.appendChild(toastEl);

    // Determine duration
    const defaultDuration = type === ToastType.Error ? ERROR_DURATION_MS : DEFAULT_DURATION_MS;
    const duration = options?.duration ?? defaultDuration;

    // Set up auto-dismiss timer
    let timerId: ReturnType<typeof setTimeout> | null = null;
    if (duration > 0) {
        timerId = setTimeout(() => {
            dismissToast(toast);
        }, duration);
    }

    const toast: ActiveToast = { element: toastEl, timerId };
    activeToasts.push(toast);

    // Close button handler
    closeEl.addEventListener('click', () => {
        dismissToast(toast);
    });

    // Trigger entrance animation on next frame
    requestAnimationFrame(() => {
        toastEl.classList.add('toast-enter');
    });
}

/**
 * Get the icon character for a toast type.
 */
function getIcon(type: ToastType): string {
    switch (type) {
        case ToastType.Info:
            return 'ℹ';
        case ToastType.Warning:
            return '⚠';
        case ToastType.Error:
            return '✕';
        case ToastType.Success:
            return '✓';
    }
}

/**
 * Dismiss all active toasts. Useful for cleanup in tests.
 */
export function dismissAllToasts(): void {
    // Copy array since dismissToast mutates it
    const toastsCopy = [...activeToasts];
    for (const toast of toastsCopy) {
        dismissToast(toast);
    }
}
